import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of, EMPTY } from 'rxjs';
import { map, switchMap, expand, takeLast, tap, catchError } from 'rxjs/operators';
import {DocumentPayload} from '../../models/document';
import { appendErpUserStationParams } from '../erp-api-query.helper';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
    private baseUrl = `${environment.document_url}api/documents`;
    constructor(private http: HttpClient) {}

    /**
     * Extrait un tableau de documents quelle que soit la forme de l’enveloppe API
     * (data[], data.documents, documents, rows, list, items, records…).
     */
    private extractDocumentsArrayFromResponse(r: any): any[] {
        if (r == null) return [];
        if (Array.isArray(r)) return r;
        if (Array.isArray(r.documents)) return r.documents;
        if (Array.isArray(r.data)) return r.data;
        const d = r.data;
        if (d != null && typeof d === 'object' && !Array.isArray(d)) {
            if (Array.isArray(d.documents)) return d.documents;
            if (Array.isArray(d.data)) return d.data;
            if (Array.isArray(d.rows)) return d.rows;
            if (Array.isArray(d.list)) return d.list;
            if (Array.isArray(d.items)) return d.items;
            if (Array.isArray(d.records)) return d.records;
        }
        return [];
    }

    private extractPaginationFromResponse(r: any): any {
        return r?.pagination ?? r?.data?.pagination ?? r?.meta ?? r?.data?.meta ?? null;
    }

    private currentPreviewDocument = new BehaviorSubject<DocumentPayload | null>(null);
    currentPreviewDocument$ = this.currentPreviewDocument.asObservable();

    setCurrentPreviewDocument(doc: DocumentPayload) {
        this.currentPreviewDocument.next(doc);
    }

    clearCurrentPreviewDocument() {
        this.currentPreviewDocument.next(null);
    }

    create(payload: DocumentPayload): Observable<any> {
        console.log('📤 [DocumentService.create] Payload envoyé au backend:', JSON.stringify(payload, null, 2));
        console.log('📤 [DocumentService.create] Totaux (à enregistrer tels quels par l\'API):', {
            total_ht: payload.total_ht,
            total_tva: payload.total_tva,
            total_ttc: payload.total_ttc,
            timbre_fiscal: payload.timbre_fiscal,
            net_payer: payload.net_payer
        });
        console.log('📤 [DocumentService.create] hidden_client_fields:', payload.hidden_client_fields);
        console.log('📤 [DocumentService.create] hidden_fournisseur_fields:', payload.hidden_fournisseur_fields);
        console.log('📤 [DocumentService.create] parametrage_doc:', JSON.stringify(payload.parametrage_doc, null, 2));
        
        if (!payload.parametrage_doc || payload.parametrage_doc === null || payload.parametrage_doc === undefined) {
            console.error('❌ [DocumentService.create] ERREUR: parametrage_doc est manquant ou null dans le payload!');
        }
        if (payload.total_ttc != null && payload.net_payer != null) {
            console.warn('⚠️ [DocumentService.create] L\'API doit persister total_ttc et net_payer tels quels (sans recalcul). Voir API-DOCUMENT-CREATE-CONTRACT.md');
        }
        
        return this.http.post(this.baseUrl, payload);

    }

    getDocumentById(id: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/${id}`);
    }

    updateStatus(id: number, statutId: number): Observable<any> {
        return this.http.patch(`${this.baseUrl}/${id}/status`, { statut_id: statutId});
    }

    updateStatut(id: number, payload: { statut_id: number; station_id: number }): Observable<any> {
        return this.http.patch(`${this.baseUrl}/${id}/statut`, payload);

    }

    getByStatutAndType(statutId: number, typeDocumentId: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/statut/${statutId}/${typeDocumentId}`);
    }

    /**
     * Documents d’un fournisseur filtrés par type de document ; statut optionnel en query.
     * GET .../api/documents/fournisseur/:fournisseur_id/type/:type_document_id?page=&limit=&station_id=&statut_id?
     * (aligné backend : ex. /fournisseur/4/type/12?page=1&limit=10&station_id=8)
     */
    getDocumentsByFournisseurTypeStatut(
        fournisseurId: number,
        typeDocumentId: number | string,
        statutId: number | string | null | undefined,
        options?: { page?: number; limit?: number; stationId?: number }
    ): Observable<any> {
        const page = options?.page ?? 1;
        const limit = options?.limit ?? 100;
        const stationId = options?.stationId ?? 0;
        const url = `${this.baseUrl}/fournisseur/${fournisseurId}/type/${Number(typeDocumentId)}`;
        let params = new HttpParams()
            .set('page', String(page))
            .set('limit', String(limit))
            .set('station_id', String(stationId));
        if (statutId != null && String(statutId).trim() !== '') {
            const n = Number(statutId);
            if (!Number.isNaN(n)) {
                params = params.set('statut_id', String(n));
            }
        }
        console.log('[DocumentService] GET fiche fournisseur + type', url, {
            page,
            limit,
            station_id: stationId,
            statut_id: statutId != null && String(statutId).trim() !== '' ? Number(statutId) : '(omis)'
        });
        return this.http.get<any>(url, { params }).pipe(
            tap((res) => {
                const docs = res?.data?.documents;
                const n = Array.isArray(docs) ? docs.length : 0;
                console.log('[DocumentService] réponse fiche fournisseur', {
                    success: res?.success,
                    nbDocuments: n,
                    pagination: res?.data?.pagination,
                    echantillon: docs?.[0]?.ref_externe ?? null
                });
            })
        );
    }

    getByStationAndType(
        stationId: number,
        typeDocumentId: number,
        page?: number,
        limit?: number,
        userId?: number
    ): Observable<any> {
        let url = `${this.baseUrl}/station/${stationId}/${typeDocumentId}`;
        let params = new HttpParams();
        if (page !== undefined) {
            params = params.set('page', page.toString());
        }
        if (limit !== undefined) {
            params = params.set('limit', limit.toString());
        }
        if (userId != null && userId > 0) {
            params = params.set('user_id', String(userId));
        }
        const hasParams = params.keys().length > 0;
        return this.http.get(url, hasParams ? { params } : {});
    }

    /** Liste documents depuis la réponse GET (enveloppes data / data.documents / etc.). */
    getDocumentsArrayFromApiResponse(r: any): any[] {
        return this.extractDocumentsArrayFromResponse(r);
    }

  
    getByStationAndTypeAll(stationId: number, typeDocumentId: number, pageSize: number = 200): Observable<any[]> {
        const toList = (r: any): any[] => {
            if (!r) return [];
            const d = r?.data?.documents ?? r?.data;
            if (Array.isArray(d)) return d;
            if (Array.isArray(r?.documents)) return r.documents;
            if (Array.isArray(r)) return r;
            return [];
        };
        const getPagination = (r: any) => r?.pagination ?? r?.data?.pagination ?? null;

        return this.getByStationAndType(stationId, typeDocumentId, 1, pageSize).pipe(
            map(r => ({ all: toList(r), page: 1, lastBatch: toList(r), pagination: getPagination(r) })),
            expand(({ all, page, lastBatch, pagination }) => {
                const totalPages = pagination ? (pagination.total_pages ?? pagination.last_page ?? 1) : null;
                const hasMoreByPagination = totalPages != null && page < totalPages;
                const hasMoreByLength = lastBatch.length >= pageSize;
                const hasMore = hasMoreByPagination || (totalPages == null && hasMoreByLength);
                if (!hasMore) return EMPTY;
                const nextPage = page + 1;
                return this.getByStationAndType(stationId, typeDocumentId, nextPage, pageSize).pipe(
                    map(res => {
                        const batch = toList(res);
                        return {
                            all: [...all, ...batch],
                            page: nextPage,
                            lastBatch: batch,
                            pagination: getPagination(res)
                        };
                    })
                );
            }),
            takeLast(1),
            map(({ all }) => all)
        );
    }

    /**
     * Tous les documents de vente de la station (devis, commande client, bons, facture) — fusion par id.
     */
    getAllVenteDocumentsForStation(stationId: number, pageSizePerType: number = 200): Observable<any[]> {
        const venteTypeIds = [7, 8, 9, 10, 11];
        const calls = venteTypeIds.map(id =>
            this.getByStationAndTypeAll(stationId, id, pageSizePerType).pipe(
                catchError(err => {
                    console.error(`[DocumentService] getByStationAndTypeAll station=${stationId} type=${id}`, err);
                    return of([]);
                })
            )
        );
        return forkJoin(calls).pipe(
            map(arrays => {
                const merged = ([] as any[]).concat(...arrays);
                const byId = new Map<number, any>();
                merged.forEach((doc: any) => {
                    const did = doc?.id;
                    if (did != null) byId.set(Number(did), doc);
                });
                const list = Array.from(byId.values());
                list.sort((a, b) => {
                    const da = new Date(a?.date || a?.date_creation || a?.created_at || 0).getTime();
                    const db = new Date(b?.date || b?.date_creation || b?.created_at || 0).getTime();
                    return db - da;
                });
                return list;
            })
        );
    }

    /**
     * Tous les documents d’achat de la station (commande fournisseur, bon réception, facture fournisseur) — fusion par id.
     */
    getAllAchatDocumentsForStation(stationId: number, pageSizePerType: number = 200): Observable<any[]> {
        const achatTypeIds = [12, 13, 14];
        const calls = achatTypeIds.map(id =>
            this.getByStationAndTypeAll(stationId, id, pageSizePerType).pipe(
                catchError(err => {
                    console.error(`[DocumentService] getByStationAndTypeAll (achat) station=${stationId} type=${id}`, err);
                    return of([]);
                })
            )
        );
        return forkJoin(calls).pipe(
            map(arrays => {
                const merged = ([] as any[]).concat(...arrays);
                const byId = new Map<number, any>();
                merged.forEach((doc: any) => {
                    const did = doc?.id;
                    if (did != null) byId.set(Number(did), doc);
                });
                const list = Array.from(byId.values());
                list.sort((a, b) => {
                    const da = new Date(a?.date || a?.date_creation || a?.created_at || 0).getTime();
                    const db = new Date(b?.date || b?.date_creation || b?.created_at || 0).getTime();
                    return db - da;
                });
                return list;
            })
        );
    }

    /**
     * Récupère la dernière référence pour un type de document et une station.
     * GET /api/documents/dernier-ref/:type_document_id?station_id=:station_id
     * Retourne ref_externe (ex: DEV-0042).
     */
    getDernierRef(typeDocumentId: number, stationId: number): Observable<{ ref_externe?: string }> {
        const url = `${this.baseUrl}/dernier-ref/${typeDocumentId}`;
        const params = new HttpParams().set('station_id', stationId.toString());
        return this.http.get<{ ref_externe?: string }>(url, { params });
    }

    /**
     * Compteur de référence interne par type / station / utilisateur.
     * GET /api/documents/ref-counter/:type_document_id?station_id=&user_id=
     */
    getRefCounter(typeDocumentId: number, stationId: number, userId: number): Observable<any> {
        const url = `${this.baseUrl}/ref-counter/${typeDocumentId}`;
        const params = new HttpParams()
            .set('station_id', String(stationId))
            .set('user_id', String(userId));
        return this.http.get<any>(url, { params });
    }

    /**
     * Incrémente le compteur après création effective d’un document.
     * PUT /api/documents/ref-counter/increment
     */
    incrementRefCounter(payload: {
        station_id: number;
        user_id: number;
        type_document_id: number;
    }): Observable<any> {
        return this.http.put<any>(`${this.baseUrl}/ref-counter/increment`, payload);
    }

    update(id: number, document: DocumentPayload): Observable<any> {
        console.log('📤 [DocumentService.update] Payload envoyé au backend:', JSON.stringify(document, null, 2));
        console.log('📤 [DocumentService.update] hidden_client_fields:', document.hidden_client_fields);
        console.log('📤 [DocumentService.update] hidden_fournisseur_fields:', document.hidden_fournisseur_fields);
        console.log('📤 [DocumentService.update] parametrage_doc:', JSON.stringify(document.parametrage_doc, null, 2));
        
        // S'assurer que parametrage_doc est toujours présent
        if (!document.parametrage_doc || document.parametrage_doc === null || document.parametrage_doc === undefined) {
            console.error('❌ [DocumentService.update] ERREUR: parametrage_doc est manquant ou null dans le payload!');
        }
        
        return this.http.put(`${this.baseUrl}/${id}`, document);
    }

    countByType(): Observable<any> {
        return this.http.get(`${this.baseUrl}/count-by-type`);
    }

    countByTypeFiltered(filters: any): Observable<any> {
        return this.http.post(`${this.baseUrl}/count-by-type/filtered`, filters);
    }

    countByStatut(stationId: number, typeDocumentId: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/count-by-statut/${stationId}/${typeDocumentId}`);
    }

    countByStatutFiltered(stationId: number, typeDocumentId: number, filters: any): Observable<any> {
        return this.http.post(`${this.baseUrl}/count-by-statut/${stationId}/${typeDocumentId}/filtered`, filters);
    }

    convertDocumentType(
        id: number,
        typeDocumentId: number,
        statutId?: number,
        documentOriginalId?: number,
        totalsAvoir?: { total_ht: number; total_tva: number; total_ttc: number; timbre_fiscal: number; net_payer: number }
    ): Observable<any> {
        const payload: any = { type_document_id: typeDocumentId };

        if (statutId !== undefined) {
            payload.statut_id = statutId;
        }
        if (documentOriginalId !== undefined) {
            payload.document_original_id = documentOriginalId;
        }
        if (totalsAvoir != null) {
            payload.total_ht = typeof totalsAvoir.total_ht === 'number' ? totalsAvoir.total_ht.toFixed(3) : totalsAvoir.total_ht;
            payload.total_tva = typeof totalsAvoir.total_tva === 'number' ? totalsAvoir.total_tva.toFixed(3) : totalsAvoir.total_tva;
            payload.total_ttc = typeof totalsAvoir.total_ttc === 'number' ? totalsAvoir.total_ttc.toFixed(3) : totalsAvoir.total_ttc;
            payload.timbre_fiscal = typeof totalsAvoir.timbre_fiscal === 'number' ? totalsAvoir.timbre_fiscal.toFixed(3) : totalsAvoir.timbre_fiscal;
            payload.net_payer = typeof totalsAvoir.net_payer === 'number' ? totalsAvoir.net_payer.toFixed(3) : totalsAvoir.net_payer;
            console.log('📤 [DocumentService.convertDocumentType] Totaux avoir envoyés au duplicate:', payload.total_ht, payload.total_tva, payload.total_ttc, payload.net_payer);
        }
        return this.http.post(`${this.baseUrl}/${id}/duplicate`, payload);
    }

    convertToAvoir(factureId: number, typeDocumentAvoirId: number, stationId?: number): Observable<any> {
        const payload: any = {
            type_document_id: typeDocumentAvoirId,
            document_original_id: factureId,
            statut_id: 1 // Brouillon
        };
        return this.http.post(`${this.baseUrl}/${factureId}/duplicate`, payload);
    }

    updateDocumentOriginalId(documentId: number, documentOriginalId: number): Observable<any> {
        return this.http.put(`${this.baseUrl}/${documentId}`, { document_original_id: documentOriginalId });
    }

    getTypeDocuments(): Observable<any> {
        return this.http.get(`${environment.document_url}api/type-documents`);
    }

    /**
     * GET /api/type-documents/:type_document_id
     * Préfixe de référence, compteur, etc. pour aligner ref_externe sur le type (sans préfixe codé en dur FAC, etc.).
     */
    getTypeDocumentById(typeDocumentId: number): Observable<any> {
        return this.http.get<any>(`${environment.document_url}api/type-documents/${typeDocumentId}`).pipe(
            map((res) => {
                const root = res?.data ?? res;
                if (root && typeof root === 'object' && !Array.isArray(root)) {
                    return (
                        root.type_document ??
                        root.typeDocument ??
                        root.document_type ??
                        root.type ??
                        root
                    );
                }
                return root;
            })
        );
    }

    /** GET /api/documents/reference-model/all — récupère tous les modèles de référence. */
    getReferenceModelsAll(): Observable<any> {
        return this.http.get(`${this.baseUrl}/reference-model/all`);
    }

    /** POST /api/documents/reference-model — crée un modèle personnalisé (template). */
    createReferenceModel(payload: {
        format_template: string;
        description?: string;
        seq_padding?: number;
        prefix?: string | null;
        station_id?: number | null;
        type_document_id?: number | null;
    }): Observable<any> {
        return this.http.post(`${this.baseUrl}/reference-model`, payload);
    }

    /**
     * GET /api/documents/reference-model/:id
     * Détail d’un modèle (prefix, model_type, description, etc.) pour aligner ref_externe et reference_model_type.
     */
    getReferenceModelById(id: number): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/reference-model/${id}`).pipe(
            map((res) => res?.data ?? res)
        );
    }

    /** Modèle de référence par défaut par station (+ optionnellement par type_document_id). */
    private defaultReferenceModelTypeByKey = new Map<string, number>();

    private refModelKey(stationId: number, typeDocumentId?: number | null): string {
        const tid = typeDocumentId != null && Number(typeDocumentId) > 0 ? Number(typeDocumentId) : 0;
        return `${stationId}:${tid}`;
    }

    setDefaultReferenceModelType(
        stationId: number,
        modelType: number | null,
        typeDocumentId?: number | null
    ): void {
        const key = this.refModelKey(stationId, typeDocumentId);
        if (modelType == null) {
            this.defaultReferenceModelTypeByKey.delete(key);
            // Compat : clé station seule
            if (typeDocumentId == null || Number(typeDocumentId) <= 0) {
                this.defaultReferenceModelTypeByKey.delete(String(stationId));
            }
        } else {
            this.defaultReferenceModelTypeByKey.set(key, modelType);
            if (typeDocumentId == null || Number(typeDocumentId) <= 0) {
                this.defaultReferenceModelTypeByKey.set(String(stationId), modelType);
            }
        }
    }

    getDefaultReferenceModelType(stationId: number, typeDocumentId?: number | null): number | null {
        const typed = this.defaultReferenceModelTypeByKey.get(this.refModelKey(stationId, typeDocumentId));
        if (typed != null) {
            return typed;
        }
        return this.defaultReferenceModelTypeByKey.get(String(stationId))
            ?? this.defaultReferenceModelTypeByKey.get(this.refModelKey(stationId, null))
            ?? null;
    }

    getStatutDocuments(): Observable<any> {
        return this.http.get(`${environment.document_url}api/statut-documents`);
    }

    /**
     * Récupère l'id d'un statut par son titre via GET /api/statut-documents.
     * Comparaison insensible à la casse et aux accents (Utilisé, utilise, etc.).
     * @param titre ex: "Utilisé", "Partiellement utilisé", "Non utilisé"
     * @returns id du statut ou null
     */
    getStatutIdByTitre(titre: string): Observable<number | null> {
        const normalize = (s: string) => (s || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const target = normalize(titre);
        return this.http.get<any>(`${environment.document_url}api/statut-documents`).pipe(
            map((res: any) => {
                const raw = res?.data ?? res;
                const list = Array.isArray(raw) ? raw : (raw?.statuts ?? []);
                const found = list.find((s: any) => normalize(s?.titre ?? s?.label ?? '') === target);
                return found?.id != null ? Number(found.id) : null;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Ids des statuts avoir (Utilisé, Partiellement utilisé) pour la mise à jour après paiement.
     * Utilise l'API statut-documents pour ne pas dépendre d'ids en dur.
     */
    getStatutIdsAvoir(): Observable<{ utiliseId: number | null; partiellementUtiliseId: number | null }> {
        const normalize = (s: string) => (s || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return this.http.get<any>(`${environment.document_url}api/statut-documents`).pipe(
            map((res: any) => {
                const raw = res?.data ?? res;
                const list = Array.isArray(raw) ? raw : (raw?.statuts ?? raw?.statut_documents ?? []);
                let utiliseId: number | null = null;
                let partiellementUtiliseId: number | null = null;
                list.forEach((s: any) => {
                    const t = normalize(s?.titre ?? s?.label ?? '');
                    if (t === 'utilise') utiliseId = s?.id != null ? Number(s.id) : null;
                    else if (t.includes('partiellement') && t.includes('utilise')) partiellementUtiliseId = s?.id != null ? Number(s.id) : null;
                });
                return { utiliseId: utiliseId ?? 18, partiellementUtiliseId: partiellementUtiliseId ?? 20 };
            }),
            catchError(() => of({ utiliseId: 18, partiellementUtiliseId: 20 }))
        );
    }

    /**
     * Liste des statuts document (Payé, Avoir, etc.) — GET /api/statut-documents uniquement.
     * Réponse typique : { data: { statuts: [...] } } ou { data: { statut_documents: [...] } }.
     */
    getStatuts(): Observable<{ id: number; titre: string; avoir?: boolean }[]> {
        const mapStatutRow = (s: any) => ({
            id: Number(s.id),
            titre: (s.titre ?? s.label ?? '').trim(),
            avoir: s.avoir === true || s.avoir === 1 || s.avoir === '1'
        });
        return this.http.get<any>(`${environment.document_url}api/statut-documents`).pipe(
            map((res: any) => {
                const raw =
                    res?.data?.statuts ??
                    res?.data?.statut_documents ??
                    res?.statuts ??
                    res?.statut_documents ??
                    (Array.isArray(res?.data) ? res.data : null) ??
                    res?.data ??
                    res;
                const list = Array.isArray(raw) ? raw : [];
                return list
                    .filter((s: any) => s != null && (s.id != null || s.id === 0))
                    .map(mapStatutRow);
            }),
            catchError(() => of([]))
        );
    }

    /**
     * Ids des statuts "avoir" (Utilisé, Non utilisé, Partiellement utilisé) et id du statut "Payé"
     * pour le relevé client. Fallback sur valeurs par défaut si l'API ne renvoie rien.
     */
    getStatutIdsForReleveClient(): Observable<{ statutsAvoirIds: number[]; statutPayeId: number | null }> {
        const fallbackAvoir = [18, 19, 20];
        const fallbackPaye = 5;
        return this.getStatuts().pipe(
            map((statuts) => {
                const byAvoirFlag = statuts.filter((s) => s.avoir === true);
                const avoirTitres = ['utilisé', 'non utilisé', 'partiellement utilisé', 'avoir'];
                const byTitres = statuts.filter((s) =>
                    avoirTitres.some((t) => (s.titre || '').toLowerCase().includes(t))
                );
                const statutsAvoirIds = (byAvoirFlag.length ? byAvoirFlag : byTitres).map((s) => s.id);
                const paye = statuts.find(
                    (s) => (s.titre || '').toLowerCase() === 'payé' || (s.titre || '').toLowerCase() === 'payee'
                );
                return {
                    statutsAvoirIds: statutsAvoirIds.length ? statutsAvoirIds : fallbackAvoir,
                    statutPayeId: paye != null ? paye.id : fallbackPaye
                };
            }),
            catchError(() => of({ statutsAvoirIds: fallbackAvoir, statutPayeId: fallbackPaye }))
        );
    }

    /**
     * Récupère l'id du type de document par son titre via GET /api/statut-documents.
     * Réponse attendue : { success, data: { types: [{ id, titre, ... }] } }.
     * Retourne null si non trouvé ou en cas d'erreur.
     */
    getTypeDocumentIdByTitre(titre: string): Observable<number | null> {
        return this.getStatutDocuments().pipe(
            map((res: any) => {
                const types = res?.data?.types ?? res?.types ?? [];
                if (!Array.isArray(types)) return null;
                const found = types.find((t: any) => (t?.titre || '').toUpperCase() === (titre || '').toUpperCase());
                return found != null && found.id != null ? Number(found.id) : null;
            }),
            tap(id => id != null && console.log('[DocumentService] Type document id pour', titre, ':', id))
        );
    }

    calculateValeurUnitaire(productId: number, quantity: number, entrepotId?: number, stationId?: number, valeurUnitaire?: number): Observable<any> {
        const payload: any = {
            product_id: productId,
            quantity: quantity
        };
        if (entrepotId) {
            payload.entrepot_id = entrepotId;
        }
        if (stationId) {
            payload.station_id = stationId;
        }
        // Ajouter la valeur unitaire modifiée si elle existe
        if (valeurUnitaire !== undefined && valeurUnitaire !== null && valeurUnitaire > 0) {
            payload.valeur_unitaire = valeurUnitaire;
            console.log('📤 [DocumentService.calculateValeurUnitaire] Valeur unitaire modifiée incluse dans le payload:', valeurUnitaire);
        }
        console.log('📤 [DocumentService.calculateValeurUnitaire] Appel API backend avec payload:', payload);
        // Appeler l'API backend pour calculer la valeur unitaire
        // Note: L'URL de l'API doit être définie selon votre backend
        return this.http.post(`${this.baseUrl}/calculate-valeur-unitaire`, payload);
    }

    /**
     * Récupérer les documents liés à un document original
     * @param documentOriginalId ID du document original
     */
    getDocumentsByOriginalId(documentOriginalId: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/original/${documentOriginalId}`);
    }

    /**
     * Récupérer le dernier prix HT d'achat pour un produit
     * @param productId ID du produit
     */
    getLastPrixHt(productId: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/lignes-document/last-prix-ht/${productId}`);
    }

    /**
     * Récupérer les paramètres du document depuis l'API
     * @param documentId ID du document
     */
    getDocumentParametrage(documentId: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/${documentId}/parametrage-doc`);
    }

    /**
     * Envoyer un email pour un document
     * @param documentId ID du document
     * @param emailData Données de l'email (to, cc, subject, message, user_id)
     */
    sendEmail(documentId: number, emailData: { to: string; cc?: string; subject: string; message: string; user_id: number }): Observable<any> {
        console.log('📧 [DocumentService.sendEmail] Envoi email pour document:', documentId);
        console.log('📧 [DocumentService.sendEmail] Données email:', emailData);
        return this.http.post(`${this.baseUrl}/${documentId}/send-email`, emailData);
    }

    /**
     * Lignes de vente (filtres optionnels produit et/ou client).
     * GET /api/documents/lignes/vente?product_id=&client_id=&page=&limit=
     */
    getDocumentsLignesVente(params?: {
        product_id?: number;
        client_id?: number;
        page?: number;
        limit?: number;
        station_id?: number;
        user_id?: number;
    }): Observable<any> {
        let httpParams = new HttpParams()
            .set('page', String(params?.page ?? 1))
            .set('limit', String(params?.limit ?? 50));
        if (params?.product_id != null && params.product_id > 0) {
            httpParams = httpParams.set('product_id', String(params.product_id));
        }
        if (params?.client_id != null && params.client_id > 0) {
            httpParams = httpParams.set('client_id', String(params.client_id));
        }
        if (params?.station_id != null && params.station_id > 0) {
            httpParams = httpParams.set('station_id', String(params.station_id));
        }
        if (params?.user_id != null && params.user_id > 0) {
            httpParams = httpParams.set('user_id', String(params.user_id));
        }
        return this.http.get<any>(`${this.baseUrl}/lignes/vente`, { params: httpParams });
    }

    /**
     * Lignes d'achat (filtres optionnels produit et/ou fournisseur).
     * GET /api/documents/lignes/achat?product_id=&fournisseur_id=&page=&limit=
     * Sans filtre, l'endpoint utilisé est /api/documents/achat/lignes?page=&limit=.
     */
    getDocumentsLignesAchat(params?: {
        product_id?: number;
        fournisseur_id?: number;
        page?: number;
        limit?: number;
        station_id?: number;
        user_id?: number;
    }): Observable<any> {
        const hasProduct = params?.product_id != null && params.product_id > 0;
        const hasFournisseur = params?.fournisseur_id != null && params.fournisseur_id > 0;

        let httpParams = new HttpParams()
            .set('page', String(params?.page ?? 1))
            .set('limit', String(params?.limit ?? 50));
        if (hasProduct) {
            httpParams = httpParams.set('product_id', String(params!.product_id));
        }
        if (hasFournisseur) {
            httpParams = httpParams.set('fournisseur_id', String(params!.fournisseur_id));
        }
        if (params?.station_id != null && params.station_id > 0) {
            httpParams = httpParams.set('station_id', String(params.station_id));
        }
        if (params?.user_id != null && params.user_id > 0) {
            httpParams = httpParams.set('user_id', String(params.user_id));
        }

        const path = hasProduct || hasFournisseur ? `${this.baseUrl}/lignes/achat` : `${this.baseUrl}/achat/lignes`;
        return this.http.get<any>(path, { params: httpParams });
    }

    /**
     * Documents par client avec filtres optionnels
     * GET /api/documents/client/:client_id?page=&limit=&statut_id=&type_document_id=&station_id=&user_id=
     */
    getDocumentsByClient(
        clientId: number,
        params?: {
            page?: number;
            limit?: number;
            statut_id?: number;
            type_document_id?: number;
            station_id?: number;
            user_id?: number;
        }
    ): Observable<{ success?: boolean; data?: any[]; documents?: any[]; pagination?: any }> {
        let httpParams = new HttpParams();
        if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
        if (params?.limit != null) httpParams = httpParams.set('limit', String(params.limit));
        if (params?.statut_id != null) httpParams = httpParams.set('statut_id', String(params.statut_id));
        if (params?.type_document_id != null) httpParams = httpParams.set('type_document_id', String(params.type_document_id));
        if (params?.station_id != null && params.station_id > 0) {
            httpParams = httpParams.set('station_id', String(params.station_id));
        }
        if (params?.user_id != null && params.user_id > 0) {
            httpParams = httpParams.set('user_id', String(params.user_id));
        }
        const options = httpParams.keys().length ? { params: httpParams } : {};
        return this.http.get<any>(`${this.baseUrl}/client/${clientId}`, options);
    }

    getDocumentsByClientAll(
        clientId: number,
        pageSize: number = 200,
        session?: { station_id?: number; user_id?: number }
    ): Observable<any[]> {
        const toList = (r: any) => this.extractDocumentsArrayFromResponse(r);
        const getPagination = (r: any) => this.extractPaginationFromResponse(r);

        const baseParams = {
            page: 1 as number,
            limit: pageSize,
            ...(session?.station_id != null && session.station_id > 0 ? { station_id: session.station_id } : {}),
            ...(session?.user_id != null && session.user_id > 0 ? { user_id: session.user_id } : {}),
        };
        return this.getDocumentsByClient(clientId, baseParams).pipe(
            tap(r => console.log('[API] GET /api/documents/client/' + clientId + '?page=1&limit=' + pageSize + ' →', toList(r).length, 'documents', '| pagination:', getPagination(r))),
            map(r => ({ all: toList(r), page: 1, lastBatch: toList(r), pagination: getPagination(r) })),
            expand(({ all, page, lastBatch, pagination }) => {
                const totalPages = pagination ? (pagination.total_pages ?? pagination.last_page ?? 1) : null;
                const hasMoreByPagination = totalPages != null && page < totalPages;
                const hasMoreByLength = lastBatch.length >= pageSize;
                const hasMore = hasMoreByPagination || (totalPages == null && hasMoreByLength);
                if (!hasMore) return EMPTY;
                const nextPage = page + 1;
                return this.getDocumentsByClient(clientId, { ...baseParams, page: nextPage, limit: pageSize }).pipe(
                    tap(res => console.log('[API] GET /api/documents/client/' + clientId + '?page=' + nextPage + '&limit=' + pageSize + ' →', toList(res).length, 'documents')),
                    map(res => {
                        const batch = toList(res);
                        return { all: [...all, ...batch], page: nextPage, lastBatch: batch, pagination: getPagination(res) };
                    })
                );
            }),
            takeLast(1),
            map(({ all }) => all),
            tap(all => console.log('[API] GET client (total toutes pages):', all.length, 'documents'))
        );
    }

    /**
     * Documents par client et statut : POST /api/documents/by-client-statut
     * Body: { client_id, statut_id, page?, limit?, station_id?, user_id? }
     */
    getDocumentsByClientStatut(
        clientId: number,
        statutId: number,
        page?: number,
        limit?: number,
        session?: { station_id?: number; user_id?: number }
    ): Observable<{
        success: boolean;
        data?: { documents: any[]; pagination?: any; client_id: number; statut_id: number; statut?: any };
    }> {
        const body: {
            client_id: number;
            statut_id: number;
            page?: number;
            limit?: number;
            station_id?: number;
            user_id?: number;
        } = { client_id: clientId, statut_id: statutId };
        if (page != null) body.page = page;
        if (limit != null) body.limit = limit;
        if (session?.station_id != null && session.station_id > 0) body.station_id = session.station_id;
        if (session?.user_id != null && session.user_id > 0) body.user_id = session.user_id;
        return this.http.post<any>(`${this.baseUrl}/by-client-statut`, body);
    }

    /**
     * Récupère TOUS les documents (toutes les pages) pour un client et un statut.
     * Pagination : soit via total_pages, soit en enchaînant les pages jusqu'à une réponse courte.
     */
    getDocumentsByClientStatutAll(
        clientId: number,
        statutId: number,
        pageSize: number = 100,
        session?: { station_id?: number; user_id?: number }
    ): Observable<any[]> {
        const toList = (r: any) => this.extractDocumentsArrayFromResponse(r);
        const getPagination = (r: any) => this.extractPaginationFromResponse(r);

        return this.getDocumentsByClientStatut(clientId, statutId, 1, pageSize, session).pipe(
            tap(r => console.log('[API] POST /api/documents/by-client-statut { client_id:', clientId, ', statut_id:', statutId, ', page: 1, limit:', pageSize, '} →', toList(r).length, 'documents', '| pagination:', getPagination(r))),
            map(r => ({ all: toList(r), page: 1, lastBatch: toList(r), pagination: getPagination(r) })),
            expand(({ all, page, lastBatch, pagination }) => {
                const totalPages = pagination
                    ? (pagination.total_pages ??
                        pagination.last_page ??
                        pagination.lastPage ??
                        pagination.totalPages ??
                        (pagination.total != null && pagination.per_page != null
                            ? Math.ceil(Number(pagination.total) / Number(pagination.per_page))
                            : null) ??
                        1)
                    : null;
                const hasMoreByPagination = totalPages != null && page < totalPages;
                const hasMoreByLength = lastBatch.length >= pageSize;
                const hasMore = hasMoreByPagination || (totalPages == null && hasMoreByLength);
                if (!hasMore) return EMPTY;
                const nextPage = page + 1;
                return this.getDocumentsByClientStatut(clientId, statutId, nextPage, pageSize, session).pipe(
                    tap(res => console.log('[API] POST /api/documents/by-client-statut { client_id:', clientId, ', statut_id:', statutId, ', page:', nextPage, ', limit:', pageSize, '} →', toList(res).length, 'documents')),
                    map(res => {
                        const batch = toList(res);
                        return { all: [...all, ...batch], page: nextPage, lastBatch: batch, pagination: getPagination(res) };
                    })
                );
            }),
            takeLast(1),
            map(({ all }) => all),
            tap(all => console.log('[API] POST by-client-statut statut_id=' + statutId + ' (total toutes pages):', all.length, 'documents'))
        );
    }

    /**
     * Documents par fournisseur avec filtres optionnels
     * GET /api/documents/fournisseur/:fournisseur_id?page=1&limit=10&statut_id=...&type_document_id=...
     */
    getDocumentsByFournisseur(
        fournisseurId: number,
        params?: { page?: number; limit?: number; statut_id?: number; type_document_id?: number; station_id?: number }
    ): Observable<{ success?: boolean; data?: any[]; documents?: any[]; pagination?: any }> {
        let httpParams = new HttpParams();
        if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
        if (params?.limit != null) httpParams = httpParams.set('limit', String(params.limit));
        if (params?.statut_id != null) httpParams = httpParams.set('statut_id', String(params.statut_id));
        if (params?.type_document_id != null) httpParams = httpParams.set('type_document_id', String(params.type_document_id));
        if (params?.station_id != null) httpParams = httpParams.set('station_id', String(params.station_id));
        const options = httpParams.keys().length ? { params: httpParams } : {};
        return this.http.get<any>(`${this.baseUrl}/fournisseur/${fournisseurId}`, options);
    }

    /**
     * Récupère TOUS les documents d'un fournisseur (toutes les pages), sans filtre statut/type.
     */
    getDocumentsByFournisseurAll(fournisseurId: number, pageSize: number = 200): Observable<any[]> {
        const toList = (r: any): any[] => {
            if (!r) return [];
            if (Array.isArray(r.data)) return r.data;
            if (Array.isArray(r.documents)) return r.documents;
            if (r.data?.documents && Array.isArray(r.data.documents)) return r.data.documents;
            if (Array.isArray(r)) return r;
            return [];
        };
        const getPagination = (r: any) => r?.pagination ?? r?.data?.pagination ?? null;

        return this.getDocumentsByFournisseur(fournisseurId, { page: 1, limit: pageSize }).pipe(
            tap(r => console.log('[API] GET /api/documents/fournisseur/' + fournisseurId + '?page=1&limit=' + pageSize + ' →', toList(r).length, 'documents', '| pagination:', getPagination(r))),
            map(r => ({ all: toList(r), page: 1, lastBatch: toList(r), pagination: getPagination(r) })),
            expand(({ all, page, lastBatch, pagination }) => {
                const totalPages = pagination ? (pagination.total_pages ?? pagination.last_page ?? 1) : null;
                const hasMoreByPagination = totalPages != null && page < totalPages;
                const hasMoreByLength = lastBatch.length >= pageSize;
                const hasMore = hasMoreByPagination || (totalPages == null && hasMoreByLength);
                if (!hasMore) return EMPTY;
                const nextPage = page + 1;
                return this.getDocumentsByFournisseur(fournisseurId, { page: nextPage, limit: pageSize }).pipe(
                    tap(res => console.log('[API] GET /api/documents/fournisseur/' + fournisseurId + '?page=' + nextPage + '&limit=' + pageSize + ' →', toList(res).length, 'documents')),
                    map(res => {
                        const batch = toList(res);
                        return { all: [...all, ...batch], page: nextPage, lastBatch: batch, pagination: getPagination(res) };
                    })
                );
            }),
            takeLast(1),
            map(({ all }) => all),
            tap(all => console.log('[API] GET fournisseur (total toutes pages):', all.length, 'documents'))
        );
    }

    /**
     * POST /api/documents/by-fournisseur-statut
     * Body: { fournisseur_id, statut_id, page?, limit? }
     * Utilisé pour afficher les documents du relevé de compte fournisseur.
     */
    getDocumentsByFournisseurStatut(fournisseurId: number, statutId: number, page?: number, limit?: number): Observable<{
        success: boolean;
        data?: { documents: any[]; pagination?: any; fournisseur_id: number; statut_id: number; statut?: any };
        documents?: any[];
        pagination?: any;
    }> {
        const body: { fournisseur_id: number; statut_id: number; page?: number; limit?: number } = { fournisseur_id: fournisseurId, statut_id: statutId };
        if (page != null) body.page = page;
        if (limit != null) body.limit = limit;
        return this.http.post<any>(`${this.baseUrl}/by-fournisseur-statut`, body);
    }

    /**
     * Récupère tous les documents (toutes les pages) pour un fournisseur et un statut.
     * Appelle POST /api/documents/by-fournisseur-statut.
     * Réponse API : { success, message, data: { fournisseur_id, statut_id, statut, documents: [...], pagination } }.
     */
    getDocumentsByFournisseurStatutAll(fournisseurId: number, statutId: number, pageSize: number = 100): Observable<any[]> {
        const toList = (r: any): any[] => {
            if (!r) return [];
            const data = r?.data ?? r?.body?.data ?? r;
            if (data && Array.isArray(data.documents)) return data.documents;
            if (Array.isArray(data)) return data;
            if (Array.isArray(r.documents)) return r.documents;
            return [];
        };
        return this.getDocumentsByFournisseurStatut(fournisseurId, statutId, 1, pageSize).pipe(
            switchMap((firstRes: any) => {
                const docs = toList(firstRes);
                const data = firstRes?.data ?? firstRes?.body?.data ?? firstRes;
                const pagination = data?.pagination ?? firstRes?.pagination;
                if (!pagination || !pagination.has_next) return of(docs);
                const totalPages = pagination.total_pages || pagination.last_page || 1;
                const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                if (remainingPages.length === 0) return of(docs);
                return forkJoin(
                    remainingPages.map(p => this.getDocumentsByFournisseurStatut(fournisseurId, statutId, p, pageSize))
                ).pipe(
                    map(responses => [...docs, ...responses.reduce((acc: any[], r) => acc.concat(toList(r)), [])])
                );
            })
        );
    }

    /**
     * Statistiques des factures pour un client
     * GET /api/documents/stats/client/:client_id
     */
    getStatsClient(clientId: number): Observable<{ success: boolean; data?: any }> {
        return this.http.get<{ success: boolean; data?: any }>(`${this.baseUrl}/stats/client/${clientId}`);
    }

    /**
     * Statistiques des factures pour un fournisseur
     * GET /api/documents/stats/fournisseur/:fournisseur_id
     */
    getStatsFournisseur(fournisseurId: number): Observable<{ success: boolean; data?: any }> {
        return this.http.get<{ success: boolean; data?: any }>(`${this.baseUrl}/stats/fournisseur/${fournisseurId}`);
    }

    /**
     * Statistiques globales des factures
     * GET /api/documents/stats/factures/globales
     */
    getStatsFacturesGlobales(stationId?: number, _userId?: number): Observable<{
        success: boolean;
        message?: string;
        data?: {
            total_ht: number;
            total_ttc: number;
            factures_payees: { nombre: number; total_ttc: number };
            factures_a_payer: {
                nombre: number;
                total_ttc: number;
                detail: {
                    factures_non_payees: { nombre: number; total_ttc: number };
                    montant_restant_factures_partiellement_payees: { nombre: number; montant_restant: number };
                };
            };
            factures_partiellement_payees?: { nombre: number; total_ttc: number; montant_restant: number };
            total_factures: number;
        };
    }> {
        let params = new HttpParams();
        params = appendErpUserStationParams(params, {
            stationId: stationId != null && stationId > 0 ? stationId : undefined
        });
        return this.http.get<{
            success: boolean;
            message?: string;
            data?: {
                total_ht: number;
                total_ttc: number;
                factures_payees: { nombre: number; total_ttc: number };
                factures_a_payer: {
                    nombre: number;
                    total_ttc: number;
                    detail: {
                        factures_non_payees: { nombre: number; total_ttc: number };
                        montant_restant_factures_partiellement_payees: { nombre: number; montant_restant: number };
                    };
                };
                factures_partiellement_payees?: { nombre: number; total_ttc: number; montant_restant: number };
                total_factures: number;
            };
        }>(`${this.baseUrl}/stats/factures/globales`, { params });
    }

    /**
     * Statistiques globales des factures fournisseur
     * GET /api/documents/stats/factures-fournisseur/globales
     */
    getStatsFacturesFournisseurGlobales(): Observable<{
        success: boolean;
        message?: string;
        data?: {
            total_ht: number;
            total_ttc: number;
            factures_payees: { nombre: number; total_ttc: number };
            factures_a_payer: {
                nombre: number;
                total_ttc: number;
                detail: {
                    factures_non_payees: { nombre: number; total_ttc: number };
                    montant_restant_factures_partiellement_payees: { nombre: number; montant_restant: number };
                };
            };
            factures_partiellement_payees?: { nombre: number; total_ttc: number; montant_restant: number };
            total_factures: number;
        };
    }> {
        return this.http.get<{
            success: boolean;
            message?: string;
            data?: {
                total_ht: number;
                total_ttc: number;
                factures_payees: { nombre: number; total_ttc: number };
                factures_a_payer: {
                    nombre: number;
                    total_ttc: number;
                    detail: {
                        factures_non_payees: { nombre: number; total_ttc: number };
                        montant_restant_factures_partiellement_payees: { nombre: number; montant_restant: number };
                    };
                };
                factures_partiellement_payees?: { nombre: number; total_ttc: number; montant_restant: number };
                total_factures: number;
            };
        }>(`${this.baseUrl}/stats/factures-fournisseur/globales`);
    }
}
